import { useGSAP } from '@gsap/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';

const useSectionAnimation = ({
  backgroundRef,
  titleRef,
  descriptionRef,
  buttonRef,
  imageUrl,
  isLoading = false,
}) => {
  const animationTimeline = useRef(null);
  const hasRevealedRef = useRef(false);
  const [isImageReady, setIsImageReady] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setIsImageReady(false);
      return;
    }

    setIsImageReady(false);
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setIsImageReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setIsImageReady(true);
    };
    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const canReveal = hasRevealedRef.current || (!isLoading && isImageReady);

  useLayoutEffect(() => {
    if (canReveal) {
      hasRevealedRef.current = true;
      if (animationTimeline.current) {
        animationTimeline.current.play();
      }
    } else {
      if (animationTimeline.current) {
        animationTimeline.current.pause();
      }
    }
  }, [canReveal]);

  useGSAP(
    () => {
      if (!backgroundRef.current) {
        return;
      }

      let buttonHeight = 0;
      if (buttonRef.current) {
        gsap.set(buttonRef.current, {
          display: 'block',
          height: 'auto',
          visibility: 'hidden',
        });
        buttonHeight = buttonRef.current.offsetHeight;
      }

      if (titleRef.current) {
        gsap.set(titleRef.current, {
          opacity: 0,
          y: 5,
        });
      }

      if (descriptionRef.current) {
        gsap.set(descriptionRef.current, {
          height: 'auto',
          opacity: 1,
          y: 0,
          overflow: 'visible',
          display: 'block',
        });

        gsap.set(descriptionRef.current, {
          opacity: 0,
          y: 5,
          clipPath: 'inset(0 0 100% 0)',
          overflow: 'hidden',
          display: 'block',
        });
      }

      if (buttonRef.current) {
        gsap.set(buttonRef.current, {
          opacity: 0,
          y: buttonHeight,
          visibility: 'visible',
          display: 'block',
          height: 'auto',
        });
      }

      const tl = gsap.timeline({
        paused: true,
      });

      animationTimeline.current = tl;

      tl.fromTo(
        backgroundRef.current,
        { scale: 1.2 },
        {
          scale: 1,
          duration: 1.8,
          ease: 'power2.out',
          onComplete: () => {
            gsap.set(document.body, { overflowX: 'auto' });
            if (backgroundRef.current) {
              backgroundRef.current.style.transform = '';
            }
          },
        },
        0,
      );

      const titleAndDescriptionStartTime = 0.5;
      const contentAnimationDuration = 1.2;
      const contentEase = 'power2.out';

      if (titleRef.current) {
        tl.to(
          titleRef.current,
          {
            opacity: 1,
            y: 0,
            duration: contentAnimationDuration,
            ease: contentEase,
          },
          titleAndDescriptionStartTime,
        );
      }

      if (descriptionRef.current) {
        tl.to(
          descriptionRef.current,
          {
            opacity: 1,
            y: 0,
            clipPath: 'inset(0% 0 0% 0)',
            duration: contentAnimationDuration,
            ease: contentEase,
          },
          titleAndDescriptionStartTime,
        );
      }

      if (buttonRef.current) {
        const buttonTweenStartTime =
          titleAndDescriptionStartTime + contentAnimationDuration;

        tl.to(
          buttonRef.current,
          {
            opacity: 1,
            y: 0,
            duration: contentAnimationDuration,
            ease: contentEase,
          },
          buttonTweenStartTime,
        );
      }

      if (canReveal) {
        tl.play();
      }
    },
    {
      scope: backgroundRef,
      dependencies: [
        canReveal,
        backgroundRef,
        titleRef,
        descriptionRef,
        buttonRef,
      ],
    },
  );

  return canReveal;
};

export default useSectionAnimation;
